import { useCallback, useEffect, useMemo } from 'react';
import { Form, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useClosingPackageCommands } from './useClosingPackageCommands';
import { useClosingPackageTaskModalData } from './useClosingPackageTaskModalData';
import {
  buildDefaultOriginalsDispatchRecipient,
  buildOriginalsDispatchBody,
  validateSendOriginalsSubmit,
} from '../lib/sendOriginalsModalValidation';
import type { OriginalsDispatchMethod } from '../types';
import { mapClosingPackageApiError } from '../lib/errorMapping';

export type SendOriginalsModalFormValues = {
  dispatchedAt: Dayjs;
  dispatchMethod: OriginalsDispatchMethod;
  trackingNumber: string;
  estimatedReceiptAt: Dayjs;
  comment: string;
};

export type UseSendOriginalsModalOptions = {
  applicationId: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

export function useSendOriginalsModal({
  applicationId,
  open,
  onClose,
  onSuccess,
}: UseSendOriginalsModalOptions) {
  const [form] = Form.useForm<SendOriginalsModalFormValues>();

  const { application, projection, contextView, contextLoading } = useClosingPackageTaskModalData({
    applicationId,
    open,
  });

  const { registerOriginalsDispatch, states } = useClosingPackageCommands();

  const defaultRecipient = useMemo(
    () => buildDefaultOriginalsDispatchRecipient(application),
    [application]
  );

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    const dispatchedAt = dayjs();
    form.setFieldsValue({
      dispatchedAt,
      dispatchMethod: 'CARRIER',
      trackingNumber: '',
      estimatedReceiptAt: dispatchedAt.add(7, 'day'),
      comment: '',
    });
  }, [form, open, applicationId]);

  const handleSubmit = useCallback(async () => {
    if (!projection) {
      message.error('Не удалось загрузить данные комплекта');
      return;
    }

    let values: SendOriginalsModalFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validationIssue = validateSendOriginalsSubmit({
      dispatchedAt: values.dispatchedAt,
      dispatchMethod: values.dispatchMethod,
      trackingNumber: values.trackingNumber,
      estimatedReceiptAt: values.estimatedReceiptAt,
      comment: values.comment,
    });

    if (validationIssue) {
      message.error(validationIssue.message);
      return;
    }

    try {
      await registerOriginalsDispatch({
        applicationId,
        body: buildOriginalsDispatchBody({
          recipient: defaultRecipient,
          dispatchMethod: values.dispatchMethod,
          trackingNumber: values.trackingNumber,
          comment: values.comment,
          dispatchedAt: values.dispatchedAt.toISOString(),
          estimatedReceiptAt: values.estimatedReceiptAt.toISOString(),
        }),
      }).unwrap();

      message.success('Отправка оригиналов зарегистрирована');
      await onSuccess?.();
      onClose();
    } catch (error) {
      message.error(mapClosingPackageApiError(error));
    }
  }, [
    applicationId,
    defaultRecipient,
    form,
    onClose,
    onSuccess,
    projection,
    registerOriginalsDispatch,
  ]);

  return {
    form,
    projection,
    contextView,
    contextLoading,
    submitting: states.registerOriginalsDispatch.isLoading,
    handleSubmit,
  };
}
